import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { membersService } from '@/services/members.service';

import MCheckbox from '@/components/ui/atoms/MCheckbox';
import { MButton } from '@/components/ui/atoms/MButtons';
import FormHeader from '@/components/ui/organisms/MHeader';
import { getMemberInfo } from '@/hooks/useMembers';
import MSpacer from '@/components/ui/atoms/MSpacer';
import { trackUserDataLoaded } from '@/utils/gtm';
import { ANALYTICS_SCREEN_TYPES, getAddMemberContactConsentScreenName } from '@/constants/analytics.constants';
import { useManagedMembersContext } from '@/context/ManagedMembersContext';
import { isChild } from '@/types/member';

const CONSENT_PARAGRAPHS = [
  'You will only create and manage profiles of family members with their consent.',
  'If you’re adding a child’s profile, you are their parent or legal guardian or authorised by their parent or legal guardian.',
];

const AddMemberConsent: React.FC = () => {
  const [checked, setChecked] = useState(Array(CONSENT_PARAGRAPHS.length).fill(false))
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const memberInfo = getMemberInfo(true);
  const { managedMembers, fetchManagedMembers } = useManagedMembersContext();
  
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    fetchManagedMembers();
  }, []);

  React.useEffect(() => {
    if (!memberInfo?.id) return;

    const saved = localStorage.getItem('add_member_form');
    let contactType: 'phone' | 'email' = 'phone'; // default
    if (saved) {
      const form = JSON.parse(saved);
      // contactOption is 'mobile' or 'email'
      contactType = form.contactOption === 'email' ? 'email' : 'phone';
    }

    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.FAMILY,
      screenName: getAddMemberContactConsentScreenName(contactType),
    });
  }, [memberInfo?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const saved = localStorage.getItem('add_member_form');
    const form = saved ? JSON.parse(saved) : {};

    // Map gender string to number for backend compatibility (1: male, 2: female, 3: other)
    const genderMap: Record<string, number> = { male: 1, female: 2, other: 3 };
    if (typeof form.gender === 'string') {
      form.gender = genderMap[form.gender.toLowerCase()] ?? null;
    }

    // Ensure BOTH family_id and current_family are set from memberInfo
    const resolvedFamilyId = memberInfo.current_family || memberInfo.familyId || null;
    form.family_id = resolvedFamilyId;
    form.current_family = resolvedFamilyId;

    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      setIsSubmitting(false);
      return;
    }
    
    // Check if this is the first family member BEFORE adding
    // managedMembers includes the logged-in user, so first member = length === 1
    const isFirstFamilyMember = managedMembers.length === 1;
    
    // Check if this is a child (no contact details)
    const isChildMember = form.dobYear && form.dobMonth && 
      isChild({ dob_year: Number(form.dobYear), dob_month: Number(form.dobMonth), dob_day: 1 });
    
    try {
      // Submit to backend
      await membersService.createMember(form);
      // Clear form and localStorage
      localStorage.removeItem('add_member_form');
      // On success, navigate to success page with appropriate flags
      navigate('/profile/family/add-member/success', { 
        state: { 
          memberName: form.firstName || form.first_name, 
          invite: form.invite, 
          nickname: form.nickname,
          isFirstFamilyMember,
          isChildOrPet: isChildMember,
        } 
      });
    } catch (err: any) {
      // Try to extract a specific backend error message
      let errorMsg = 'Failed to add member.';
      if (err?.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err?.message) {
        errorMsg = err.message;
      }
      setError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCheck = (idx: number) => {
    setChecked(prev => prev.map((v, i) => (i === idx ? !v : v)));
  };

  const allChecked = checked.every(Boolean);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const validate = (form: any) => {
    const errs: Record<string, string> = {};
    if (!form.firstName) errs.firstName = 'First name is required';
    if (!form.lastName) errs.lastName = 'Last name is required';
    if (form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) errs.email = 'Invalid email';
    // Add more camelCase checks as needed for other required fields
    return errs;
  };

  return (
    <div className="profile-page-root">
      <FormHeader
        module="Family"
        memberInfo={memberInfo}
        title="Let's get your consent"
        description={
          <>
          We need your consent before adding this member to your family.
          <br />
          <br />
          By proceeding, you confirm that:
        </>
        }
        hideLearnMore
      />
      <MSpacer />
      <div className="flex flex-col gap-6">
        {CONSENT_PARAGRAPHS.map((text, idx) => (
          <div
            key={idx}
            className="consent-paragraph"
            style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}
            onClick={e => {
              // Only toggle if the click is NOT from the checkbox itself
              if ((e.target as HTMLElement).tagName !== 'INPUT') {
                handleCheck(idx);
              }
            }}
            role="button"
            tabIndex={0}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') handleCheck(idx); }}
          >
            <MCheckbox
              checked={checked[idx]}
              onChange={() => handleCheck(idx)}
              tabIndex={-1}
            />
            <span>{text}</span>
          </div>
        ))}
      </div>
      <div className="consent-paragraph" style={{ marginTop: 48, }}>
        Learn how we protect your data and manage health information on Maatra.
      </div>
      <div style={{ marginBottom: 0, display: 'flex' }}>
        <MButton
          variant="link"
          size="md"
          className='body-s-medium'
          onClick={() => window.open('https://maatra.health/privacy', '_blank', 'noopener noreferrer')}
        >
          View our full Privacy Policy
        </MButton>
      </div>
      {/* Show form validation errors */}
      {errors && Object.keys(errors).length > 0 && (
        <div className="text-red-600 text-sm font-semibold mt-4">
          {Object.values(errors).map((err, idx) => (
            <div key={idx}>{err}</div>
          ))}
        </div>
      )}
      {error && (
        <div className="text-red-600 text-sm font-semibold mt-4 text-center">
          {error}
        </div>
      )}
      <form onSubmit={handleSubmit} className='confirm-footer'>
        <MButton
          type="submit"
          variant="filled"
          size="lg"
          className="w-full"
          disabled={isSubmitting || !allChecked}
        >
          Done
        </MButton>
      </form>
    </div>
  );
};

export default AddMemberConsent;
